import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useOutletContext } from "react-router-dom";
import { AlertCircle, ArrowLeft, Archive, Check, CreditCard, Compass, User as UserIcon } from "lucide-react";
import { MELT_BUTTON } from "../../components/ui/melt";
import type { User } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../../firebase";
import type { FirestoreUserData } from "../../types/user";
import { cn } from "../../lib/utils";
import IconButtonWithTooltip from "../../components/IconButtonWithTooltip";
import FirstSyncReveal from "../../components/FirstSyncReveal";

type SettingsContext = {
  user: User | null;
  userRecord: FirestoreUserData | null;
  userRecordLoading: boolean;
};

const NAV = [
  { name: "Profile", icon: UserIcon, to: "/settings/profile" },
  { name: "Job search", icon: Compass, to: "/settings/job-search" },
  { name: "Plan", icon: CreditCard, to: "/settings/plan" },
  { name: "Archives", icon: Archive, to: "/settings/archives" },
];

// Settings is its own page, outside the app shell: a settings-only sidebar with
// a way back, and one section per page on the right.
export default function SettingsLayout() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [userRecord, setUserRecord] = useState<FirestoreUserData | null>(null);
  const [userRecordLoading, setUserRecordLoading] = useState(true);

  useEffect(() => {
    let unsubscribeDoc: (() => void) | null = null;
    const unsubscribeAuth = auth.onAuthStateChanged((currentUser) => {
      unsubscribeDoc?.();
      unsubscribeDoc = null;
      setUser(currentUser);
      if (!currentUser) {
        navigate("/login", { replace: true });
        return;
      }
      unsubscribeDoc = onSnapshot(
        doc(db, "users", currentUser.uid),
        (snapshot) => {
          setUserRecord(snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as FirestoreUserData) : null);
          setUserRecordLoading(false);
        },
        (error) => {
          console.error("Failed to load user record:", error);
          setUserRecordLoading(false);
        }
      );
    });
    return () => {
      unsubscribeDoc?.();
      unsubscribeAuth();
    };
  }, [navigate]);

  const context: SettingsContext = { user, userRecord, userRecordLoading };

  return (
    <div className="flex h-screen overflow-hidden bg-white text-melt-text1 max-md:h-auto max-md:min-h-screen max-md:flex-col max-md:overflow-visible">
      <aside className="flex w-60 flex-none flex-col border-r border-melt-text1/[.09] bg-melt-sidebar max-md:w-full max-md:border-b max-md:border-r-0">
        {/* Header: back to the app + title, where the app sidebar has its logo */}
        <div className="flex items-center gap-1.5 px-4 pb-4 pt-5">
          <IconButtonWithTooltip
            label="Back to Caramel"
            onClick={() => navigate("/dashboard/home")}
            className="grid h-8 w-8 flex-none place-items-center rounded-md text-melt-text2 transition hover:bg-melt-text1/[.06] hover:text-melt-text1"
          >
            <ArrowLeft size={18} />
          </IconButtonWithTooltip>
          <p className="text-lg font-semibold text-melt-text1">Settings</p>
        </div>

        <nav className="px-3 pb-3 max-md:pb-2">
          <div className="space-y-px max-md:flex max-md:gap-1 max-md:space-y-0 max-md:overflow-x-auto">
            {NAV.map(({ name, icon: Icon, to }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    "flex w-full items-center gap-3 rounded-md px-3 py-1.5 text-sm text-melt-text1 transition max-md:w-auto max-md:flex-none",
                    isActive ? "bg-melt-text1/[.08] font-medium" : "hover:bg-melt-text1/[.05]"
                  )
                }
              >
                <Icon size={18} />
                <span className="whitespace-nowrap">{name}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </aside>

      <main className="scrollbar-thin min-w-0 flex-1 overflow-y-auto px-6 py-10 font-grotesk md:px-12 md:py-12">
        <div className="mx-auto w-full max-w-[760px]">
          <Outlet context={context} />
          <FirstSyncReveal userRecord={userRecord} />
        </div>
      </main>
    </div>
  );
}

export function useSettings() {
  return useOutletContext<SettingsContext>();
}

/** Page title and intro, like the screenshot: large title, muted lead text. */
export function SettingsHeader({ title, description }: { title: string; description: string }) {
  return (
    <header className="mb-7">
      <h1 className="text-[30px] font-semibold leading-tight tracking-[-.03em] text-melt-text1">{title}</h1>
      <p className="mt-2 max-w-[600px] text-[15px] leading-relaxed text-melt-text2">{description}</p>
    </header>
  );
}

/** White card with a hairline border, the container for each group of fields. */
export const SETTINGS_CARD = "rounded-2xl border border-melt-hair bg-white";

/** Save button with its result: "Saved" briefly, or the error. */
export function SaveRow({
  onSave,
  saving,
  saved,
  error,
  disabled,
}: {
  onSave: () => void;
  saving: boolean;
  saved: boolean;
  error: string;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <button type="button" onClick={onSave} disabled={disabled || saving} className={MELT_BUTTON}>
          {saving ? "Saving…" : "Save changes"}
        </button>
        {saved && (
          <span className="flex items-center gap-1.5 text-sm text-[#2F6B54]">
            <Check size={16} strokeWidth={1.75} />
            Saved
          </span>
        )}
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
    </div>
  );
}

export function ErrorNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 rounded-[10px] bg-[#F7E6DE] px-4 py-3 text-sm text-[#9A4F33]">
      <AlertCircle size={16} strokeWidth={1.75} className="flex-none" />
      {children}
    </div>
  );
}

/** Save state for a settings form: saving flag, a "Saved" that fades after 3s, and errors. */
export function useSaveState() {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!saved) return;
    const t = window.setTimeout(() => setSaved(false), 3000);
    return () => window.clearTimeout(t);
  }, [saved]);

  const run = async (save: () => Promise<void>) => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await save();
      setSaved(true);
    } catch (e: any) {
      setError(e?.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  return { saving, saved, error, run };
}
